/*
  VORYNEX About — scroll reveals, self-drawing lines, counting stats.

  Deliberately no WebGL here. The dark pages carry the 3D mark and the particle
  field; a story page opening on a spinning object would compete with the words
  it is supposed to introduce. What this page animates instead is quieter and
  cheaper: blocks arrive as they enter view, two SVG line drawings draw
  themselves once, and the reach figures count up. Everything is transform and
  opacity, and every observer disconnects once its element has fired.

  The one rule this file exists to honour: nothing runs unattended. There is no
  scroll listener and no persistent rAF loop. IntersectionObserver fires, the
  element animates, the observer stops watching it. The counters are the only
  timed work and they stop the moment they reach their target.
*/
(() => {
  const root = document.querySelector('[data-vpx-about]');
  if (!root) return;

  const reduceMotion =
    window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Measure every dashed path so the draw-on reveal starts fully retracted.
     getTotalLength has to be read from the live DOM — a hardcoded dasharray
     is wrong the moment the viewBox or the path changes. */
  const primeStroke = (svg) => {
    svg.querySelectorAll('path, line').forEach((el) => {
      let len = 0;
      try {
        len = el.getTotalLength ? el.getTotalLength() : 0;
      } catch (e) {
        len = 0;
      }
      if (!len && el.tagName === 'line') {
        const dx = (+el.getAttribute('x2') || 0) - (+el.getAttribute('x1') || 0);
        const dy = (+el.getAttribute('y2') || 0) - (+el.getAttribute('y1') || 0);
        len = Math.hypot(dx, dy);
      }
      if (len) el.style.setProperty('--len', Math.ceil(len));
    });
  };
  root.querySelectorAll('svg[data-ab-draw]').forEach(primeStroke);

  /* Count a figure up to its target. Runs on rAF, writes only when the
     rendered string actually changes, and stops itself at the target — the
     counter never becomes a loop that outlives its own animation. */
  const countUp = (el) => {
    const raw = (el.dataset.abCount || '').trim();
    const target = parseFloat(raw);
    const suffix = el.dataset.abSuffix || '';
    // parseFloat silently truncates anything that isn't a clean number --
    // "1–2" (a range, not a count) became a wrong, counted-up "1". Only
    // animate when the whole string round-trips back to the same number;
    // otherwise show the literal value with no animation.
    if (!isFinite(target) || String(target) !== raw) {
      el.textContent = raw + suffix;
      return;
    }
    if (reduceMotion) { el.textContent = target + suffix; return; }

    const DURATION = 1400;
    let start = 0;
    let last = '';
    const step = (now) => {
      if (!start) start = now;
      const t = Math.min(1, (now - start) / DURATION);
      const eased = 1 - Math.pow(1 - t, 3); // ease-out: fast, then arrives
      const text = Math.round(target * eased) + suffix;
      if (text !== last) { last = text; el.textContent = text; }
      if (t < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  };

  /* One observer for every revealed block. `is-in` triggers the entrance;
     `is-settled` lands afterwards and exists purely to retire the stagger
     delays, so a hover on the fourth pillar is not still waiting out an
     entrance delay from half a minute ago. */
  const io = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (!entry.isIntersecting) return;
      const el = entry.target;
      el.classList.add('is-in');
      el.querySelectorAll('[data-ab-count]').forEach(countUp);
      setTimeout(() => el.classList.add('is-settled'), 1600);
      io.unobserve(el);
    });
  }, { threshold: 0.16, rootMargin: '0px 0px -8% 0px' });

  root.querySelectorAll('[data-ab-in]').forEach((el) => io.observe(el));

  /* The drifting background is the only looping animation on the page. Pause
     it when the page is hidden. animation-play-state does not inherit, so the
     rule has to reach pseudo-elements explicitly — setting it on a container
     silently never arrives. */
  document.addEventListener('visibilitychange', () => {
    document.body.classList.toggle('ab-paused', document.visibilityState !== 'visible');
  });
})();
